import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Copy } from 'lucide-react'
import AppShell from '../../components/b2b/AppShell'
import { api } from '../../api/client'
import { QUIZ_REGISTRY } from '../../config/quizzes'

export default function QuizzesListScreen() {
  const [summaryBySlug, setSummaryBySlug] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api.get('/api/b2b/quiz/summary')
      .then((rows) => {
        if (cancelled) return
        setSummaryBySlug(Object.fromEntries(rows.map((r) => [r.slug, r])))
      })
      .catch(() => { if (!cancelled) setError('Could not load quiz data. Try again.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const quizzes = Object.values(QUIZ_REGISTRY)
  const active = quizzes.filter((q) => (q.status ?? 'active') === 'active')
  const completed = quizzes.filter((q) => q.status === 'completed')

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 md:py-12 max-w-5xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-wider text-ink-3">B2B</p>
        <h1 className="mt-1 text-2xl md:text-3xl font-bold text-ink-1">Quizzes</h1>
        <p className="mt-1 text-sm text-ink-3">
          Lead-gen and ICP-research surveys — see who's answering and what they're telling you.
        </p>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {loading && <p className="mt-8 text-xs text-ink-3">Loading…</p>}

        <QuizSection title="Active" quizzes={active} summaryBySlug={summaryBySlug} />
        <QuizSection title="Completed" quizzes={completed} summaryBySlug={summaryBySlug} />
      </div>
    </AppShell>
  )
}

function QuizSection({ title, quizzes, summaryBySlug }) {
  return (
    <div className="mt-10">
      <h2 className="text-xs font-bold uppercase tracking-wider text-ink-3">{title}</h2>
      {quizzes.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3">No {title.toLowerCase()} quizzes.</p>
      ) : (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          {quizzes.map((quiz) => (
            <QuizCard key={quiz.slug} quiz={quiz} summary={summaryBySlug[quiz.slug]} />
          ))}
        </div>
      )}
    </div>
  )
}

function QuizCard({ quiz, summary }) {
  const total = summary?.total ?? 0
  const completionPct = summary ? Math.round(summary.completion_rate * 100) : 0
  const quizUrl = `${window.location.origin}/quiz/${quiz.slug}`

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(quizUrl)
    } catch {
      // Fallback — most modern browsers permit this from a button click.
    }
  }

  return (
    <div className="bg-white rounded-3xl shadow-card hover:shadow-card-hover p-6 transition">
      <Link to={`/b2b/quizzes/${quiz.slug}`} className="group block">
        <h3 className="text-lg font-bold text-ink-1">{quiz.title}</h3>
        <div className="mt-4 flex items-center gap-6">
          <div>
            <p className="text-xl font-bold text-ink-1">{total}</p>
            <p className="text-[11px] uppercase tracking-wider font-bold text-ink-3">Respondents</p>
          </div>
          <div>
            <p className="text-xl font-bold text-ink-1">{completionPct}%</p>
            <p className="text-[11px] uppercase tracking-wider font-bold text-ink-3">Completion</p>
          </div>
        </div>
        <p className="mt-4 text-sm font-bold text-accent group-hover:underline">View report →</p>
      </Link>
      <button
        type="button"
        onClick={copyLink}
        className="mt-4 inline-flex items-center gap-1 text-ink-2 hover:text-ink-1 text-xs font-bold"
        aria-label="Copy quiz link"
        title={quizUrl}
      >
        <Copy className="w-3.5 h-3.5" />
        Copy quiz link
      </button>
    </div>
  )
}
